import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import api from "api";
import PolicyFilter from "./Filter/PolicyFilter";

function AddPoliciesPopup({
  open,
  handleClose,
  reloadParent,
  releaseKey,
  releaseNumber,
  mappedPolicies,
  availablePolicies,
  canUpdatePolicies,
}) {
  const [loading, setLoading] = useState(false);
  const [displayMessage, setDisplayMessage] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState(availablePolicies);

  useEffect(() => {
    const applyFilter = () => {
      if (selectedFilter === "All") {
        setFilteredPolicies(availablePolicies);
        return;
      }

      if (selectedFilter === "Other") {
        setFilteredPolicies(
          availablePolicies.filter((policy) =>
            ["ICD-10", "HCPCS", "Variance", "Retire"].includes(
              policy.categoryOfWorkName
            )
          )
        );
        return;
      }

      if (selectedFilter === "BreakFix") {
        setFilteredPolicies(
          availablePolicies.filter(
            (policy) => policy.categoryOfWorkName === "BreakFix"
          )
        );
        return;
      }

      // Filtering for PLA & CAB by year and quarter
      const [category, year, quarter] = selectedFilter.split(" - ");
      setFilteredPolicies(
        availablePolicies.filter(
          (policy) =>
            policy.categoryOfWorkName === category &&
            policy.year.toString() === year &&
            policy.quarter === quarter
        )
      );
    };

    applyFilter();
  }, [selectedFilter, availablePolicies]);

  const handleDisplayMessageClose = () => {
    setDisplayMessage(null);
  };

  const handleToggle = (policyKey) => {
    setSelectedPolicies((prev) =>
      prev.includes(policyKey)
        ? prev.filter((key) => key !== policyKey)
        : [...prev, policyKey]
    );
  };

  const handleSelectAll = () => {
    if (selectedPolicies.length === filteredPolicies.length) {
      setSelectedPolicies([]); // Deselect all
    } else {
      setSelectedPolicies(filteredPolicies.map((policy) => policy.policyKey)); // Select all filtered policies
    }
  };

  const handleBeforeClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    setSelectedPolicies([]);
    handleClose();
  };

  const clearSelectedPoliciesBeforeClose = () => {
    setSelectedPolicies([]);
    handleClose();
  };

  const handleConfirm = async () => {
    if (selectedPolicies.length === 0) {
      setDisplayMessage({
        message: "Please select at least one policy",
        title: "Validation",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await api.ReleaseSchedule.addPoliciesToRelease(
        releaseKey,
        selectedPolicies
      );
      setDisplayMessage({
        message:
          response.data.message || "Policy Added to release successfully",
        title: "Success",
      });
      reloadParent();
      clearSelectedPoliciesBeforeClose();
    } catch (error) {
      console.error("Error updating policies:", error);
      setDisplayMessage({
        message:
          error.response?.data?.errors ||
          "Failed to add policies to the release. Please try again.",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <CircularProgress />
        </Box>
      )}
      {displayMessage && (
        <Dialog open onClose={handleDisplayMessageClose}>
          <DialogTitle>{displayMessage.title}</DialogTitle>
          <DialogContent>{displayMessage.message}</DialogContent>
          <DialogActions>
            <Button onClick={handleDisplayMessageClose}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog open={open} onClose={handleBeforeClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography fontWeight="bold">
            Add Policies to {releaseNumber}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <PolicyFilter
            availablePolicies={availablePolicies}
            onFilterChange={setSelectedFilter}
          />

          <Box
            sx={{
              height: 400,
              overflowY: "auto",
              border: "1px solid #ddd",
              borderRadius: 2,
              p: 1,
            }}
          >
            <List dense>
              {canUpdatePolicies && (
                <ListItemButton onClick={handleSelectAll}>
                  <ListItemIcon>
                    <Checkbox
                      sx={{ padding: 0 }}
                      edge="start"
                      checked={
                        selectedPolicies.length === filteredPolicies.length
                      }
                      indeterminate={
                        selectedPolicies.length > 0 &&
                        selectedPolicies.length < filteredPolicies.length
                      }
                      tabIndex={-1}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Select All"
                    sx={{ fontWeight: "bold" }}
                  />
                </ListItemButton>
              )}

              {filteredPolicies.map((policy) => (
                <ListItemButton
                  key={policy.policyKey}
                  onClick={
                    canUpdatePolicies
                      ? () => handleToggle(policy.policyKey)
                      : undefined
                  }
                >
                  {canUpdatePolicies && (
                    <ListItemIcon>
                      <Checkbox
                        sx={{ padding: 0 }}
                        edge="start"
                        checked={selectedPolicies.includes(policy.policyKey)}
                        tabIndex={-1}
                      />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={[
                      policy.policyNumber && `${policy.policyNumber}`,
                      policy.policyTitleName && `${policy.policyTitleName}`,
                      policy.versionText && `${policy.versionText}`,
                      policy.categoryOfWorkName &&
                        `${policy.categoryOfWorkName}`,
                    ]
                      .filter(Boolean)
                      .join(" - ")}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={clearSelectedPoliciesBeforeClose} color="primary">
            CANCEL
          </Button>
          {canUpdatePolicies && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? "Saving..." : "CONFIRM & CONTINUE"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AddPoliciesPopup;
